import { useEffect, useMemo, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import type { DraftWorkItem } from '../common/types';
import { toJiraCreateItems } from '../utils/toJiraCreateItems';

/**
 * Publishes draft work items context to the chat store whenever they change.
 * This allows the chat to access Jira context for prompts.
 */

const PUBLISH_TYPE = 'jira-create-context-payload';
const PUBLISH_SOURCE = 'useJiraContext';

export const useJiraContext = ({
	isActiveMessage = true,
	draftWorkItems,
}: {
	isActiveMessage?: boolean;
	draftWorkItems: Record<string, DraftWorkItem>;
}): void => {
	const publish = usePublish('ai-mate');

	const previousDataRef = useRef<string>('');
	const previousIsActiveMessageRef = useRef<boolean>(false);

	const draftWorkItemsToSend = useMemo(() => toJiraCreateItems(draftWorkItems), [draftWorkItems]);

	useEffect(() => {
		if (!isActiveMessage) {
			return;
		}

		// Serialize the data to check if it actually changed
		const currentData = JSON.stringify(draftWorkItemsToSend);

		// Only publish if the serialized data has actually changed
		if (currentData !== previousDataRef.current) {
			previousDataRef.current = currentData;
			publish({
				type: PUBLISH_TYPE,
				source: PUBLISH_SOURCE,
				data: {
					draftWorkItems: draftWorkItemsToSend,
				},
			});
		}
	}, [draftWorkItemsToSend, isActiveMessage, publish]);

	useEffect(() => {
		// If the message becomes inactive, clear the context from the chat store
		if (!isActiveMessage && previousIsActiveMessageRef.current) {
			publish({
				type: PUBLISH_TYPE,
				source: PUBLISH_SOURCE,
				data: {
					draftWorkItems: null,
				},
			});
		}
		previousIsActiveMessageRef.current = isActiveMessage;
	}, [isActiveMessage, publish]);
};
